<template>
  <div :class="['check-box', { selected: checked }]">
    <!-- if no slot content is passed, don't render a label, because the parent component might add one -->
    <component :is="hasSlotDefault ? 'label' : 'div'" class="check-box__label">
      <div class="check-box__input-wrapper">
        <input
          ref="input"
          class="check-box__input"
          type="checkbox"
          :checked="checked"
          :disabled="disabled"
          @change="$emit('update:checked', $event.target.checked)"
        />
        <CheckIcon12 class="check-box__icon" />
      </div>

      <span
        v-if="hasSlotDefault"
        class="check-box__content"
        :class="{
          'check-box__content--no-wrap': !wrapText,
          'check-box__content--start': position === 'end',
        }"
      >
        <slot />
      </span>
    </component>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CheckIcon12 from '@components/Icons/CheckIcon12.vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'CheckBox',
  emits: { 'update:checked': null },

  components: {
    CheckIcon12,
  },

  props: {
    checked: {
      type: Boolean,
      default: false,
      required: false,
    },

    /**
     * If true, the text inside the label will wrap.
     * If false, it will show an ellipsis
     */
    wrapText: {
      type: Boolean,
      default: false,
    },

    /**
     * Position of the checkbox
     */
    position: {
      type: String as PropType<'start' | 'end'>,
      default: 'start',
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    hasSlotDefault() {
      return hasSlotContent(this.$slots.default, {});
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
.check-box__label {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 100%;

  &:not(.disabled) {
    cursor: pointer;
  }
}

// this is terrible, will be removed when we refactor the selection list
.selection-list__input--element > .check-box__label {
  height: 32px;
}

.check-box__content {
  flex: 1 1 100%;

  &.check-box__content--no-wrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.check-box__content--start {
    order: -1;
  }
}

.check-box__input-wrapper {
  position: relative;
  display: flex;
}

.check-box__icon {
  display: block;
  position: absolute;
  opacity: 0;
  top: calc(50% - 5px);
  width: 10px;
  height: auto;
  right: 3px;
  color: colors.$color-white;
  pointer-events: none;
  transition: all 0.2s ease-in-out;
}

.check-box__input {
  flex: 0 0 16px;
  border-radius: 4px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 16px;
  height: 16px;
  box-shadow: inset 0 0 0 1px colors.$color-grey-200;
  outline: none;
  transition: all 0.2s ease-in-out;
  background: colors.$color-white;

  &:not(:disabled) {
    cursor: pointer;

    &:active,
    &:hover {
      background: colors.$color-blue-50;
      box-shadow: inset 0 0 0 1px colors.$color-blue-600;
    }

    &:focus-visible {
      box-shadow: settings.$box-shadow-focus;
    }

    &:focus-visible:active,
    &:focus-visible:hover {
      box-shadow: settings.$box-shadow-border-blue, settings.$box-shadow-focus;
    }

    &:focus-visible:checked {
      box-shadow: settings.$box-shadow-border-blue, settings.$box-shadow-focus;
      background: colors.$color-blue;
    }

    &:checked {
      box-shadow: settings.$box-shadow-border-blue;
      background: colors.$color-blue;
    }

    &:focus-visible + .check-box__icon,
    &:hover + .check-box__icon {
      opacity: 1;
      color: colors.$color-blue-200;
    }

    &:checked + .check-box__icon {
      opacity: 1;
      color: colors.$color-white;
    }
  }

  &:disabled {
    background: colors.$color-grey-200;
    box-shadow: settings.$box-shadow-border-grey-50;
    cursor: not-allowed;

    &:checked {
      background: colors.$color-blue-300;
      box-shadow: none;
    }

    &:checked + .check-box__icon {
      opacity: 1;
      color: colors.$color-white;
    }
  }
}
</style>
